import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Vote } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Empty, Err, Panel } from "@/components/community/kit";
import { allows } from "@/lib/community";
import { ElectionCard } from "@/components/community/ElectionCard";
import { Motions } from "@/components/community/Motions";
import { Polls } from "@/components/community/Polls";

const blankSeat = () => ({ title: "", role: "" });

export const Votes = ({ community }) => {
  const cid = community.id;
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [roster, setRoster] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState({
    title: "", description: "", seats: [blankSeat()], nominations_close: "", voting_closes: "",
    dues_required: false, self_nominate: true,
  });
  const canManage = allows(community, "elections", "full") || community.platform_admin;

  const load = useCallback(async () => {
    try {
      const [{ data: res }, { data: r }] = await Promise.all([
        api.get(`/communities/${cid}/elections`),
        api.get(`/communities/${cid}/roster`),
      ]);
      setData(res);
      setRoster(r.members);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy("create"); setError("");
    try {
      await api.post(`/communities/${cid}/elections`, {
        ...draft,
        seats: draft.seats.filter((s) => s.title.trim().length > 1),
      });
      setDraft({ title: "", description: "", seats: [blankSeat()], nominations_close: "",
        voting_closes: "", dues_required: false, self_nominate: true });
      setComposing(false);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  return (
    <div data-testid="community-votes" className="space-y-5">
      <Err testId="votes-error">{error}</Err>

      {canManage && (
        <Panel testId="election-compose" title="Hold an election"
          sub="Nominations, secret ballots, and the winner gets the role when you declare it."
          action={
            <Button data-testid="election-compose-toggle" size="sm" variant="outline"
              onClick={() => setComposing((v) => !v)}>
              {composing ? "Cancel" : <><Vote className="mr-1.5 h-3.5 w-3.5" /> New election</>}
            </Button>
          }>
          {composing && (
            <div className="space-y-3">
              <Input data-testid="election-title" placeholder="2026 leadership election"
                value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              <Textarea data-testid="election-description" rows={3}
                placeholder="How it works, who can stand, anything members should know."
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })} />

              <div className="space-y-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Positions being filled
                </p>
                {draft.seats.map((seat, i) => (
                  <div key={i} className="flex flex-col gap-2 sm:flex-row">
                    <Input data-testid={`seat-title-${i}`} placeholder="President"
                      value={seat.title}
                      onChange={(e) => {
                        const seats = [...draft.seats];
                        seats[i] = { ...seat, title: e.target.value };
                        setDraft({ ...draft, seats });
                      }} />
                    <select data-testid={`seat-role-${i}`} value={seat.role}
                      onChange={(e) => {
                        const seats = [...draft.seats];
                        seats[i] = { ...seat, role: e.target.value };
                        setDraft({ ...draft, seats });
                      }}
                      className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                      <option value="">Title only — no extra access</option>
                      <option value="leader">Grants Leader access</option>
                      <option value="treasurer">Grants Treasurer access</option>
                      <option value="moderator">Grants Moderator access</option>
                    </select>
                  </div>
                ))}
                <Button data-testid="seat-add" size="sm" variant="ghost"
                  onClick={() => setDraft({ ...draft, seats: [...draft.seats, blankSeat()] })}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Another position
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-slate-500">
                  Nominations close
                  <Input data-testid="election-nominations-close" type="datetime-local"
                    className="mt-1" value={draft.nominations_close}
                    onChange={(e) => setDraft({ ...draft, nominations_close: e.target.value })} />
                </label>
                <label className="text-xs text-slate-500">
                  Voting closes
                  <Input data-testid="election-voting-closes" type="datetime-local"
                    className="mt-1" value={draft.voting_closes}
                    onChange={(e) => setDraft({ ...draft, voting_closes: e.target.value })} />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input data-testid="election-dues-required" type="checkbox" className="h-4 w-4"
                  checked={draft.dues_required}
                  onChange={(e) => setDraft({ ...draft, dues_required: e.target.checked })} />
                Only members whose dues are up to date may vote
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input data-testid="election-self-nominate" type="checkbox" className="h-4 w-4"
                  checked={draft.self_nominate}
                  onChange={(e) => setDraft({ ...draft, self_nominate: e.target.checked })} />
                Members may put their own name forward
              </label>

              <Button data-testid="election-create" onClick={create}
                disabled={draft.title.trim().length < 3
                  || !draft.seats.some((s) => s.title.trim().length > 1) || busy === "create"}>
                {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Open nominations
              </Button>
            </div>
          )}
        </Panel>
      )}

      {data.elections.length === 0 ? (
        <Empty testId="elections-empty">No elections have been held yet.</Empty>
      ) : (
        data.elections.map((e) => (
          <ElectionCard key={e.id} election={e} communityId={cid} roster={roster}
            canManage={canManage} myUserId={String(user?.id || "")} onChanged={load} />
        ))
      )}

      <Motions community={community} />
      <Polls community={community} />
    </div>
  );
};
